import type { AxiosInstance } from "axios";
import { AxiosError } from "axios";
import { AuthTokenError } from "../errors/authTokenError";
import { AUTH_KEYS } from "./authKeys";

let onUnauthorized: (() => void) | null = null;
let onRefreshBeforeUnauthorized: (() => Promise<boolean>) | null = null;
/** Evita múltiplos 401 paralelos dispararem vários redirects/toasts. */
let unauthorizedAlreadyHandled = false;
let refreshInProgress = false;

type RetriableAxiosConfig = NonNullable<AxiosError["config"]> & {
  _sessionRetry?: boolean;
};

/** Só encerra a sessão quando o JWT do techFormV2 falhou — não em 401 de APIs externas (RC/Flow). */
function isSessionJwtUnauthorized(error: AxiosError): boolean {
  const data = error.response?.data as Record<string, unknown> | undefined;
  const message = typeof data?.message === "string" ? data.message : "";
  const errorMsg = typeof data?.error === "string" ? data.error : "";

  if (message === "Unauthorized" && data?.status === "error") return true;
  if (message === "Unauthorized" && !errorMsg) return true;

  if (errorMsg === "Token não informado") return false;
  if (errorMsg && errorMsg !== "Unauthorized") return false;

  return !localStorage.getItem(AUTH_KEYS.SESSION_TOKEN);
}

export function setSessionUnauthorizedHandler(handler: (() => void) | null) {
  onUnauthorized = handler;
}

export function setRefreshBeforeUnauthorizedHandler(
  handler: (() => Promise<boolean>) | null
) {
  onRefreshBeforeUnauthorized = handler;
}

export function resetUnauthorizedHandledFlag() {
  unauthorizedAlreadyHandled = false;
}

async function notifySessionUnauthorized() {
  if (unauthorizedAlreadyHandled || !onUnauthorized) return;

  if (onRefreshBeforeUnauthorized && !refreshInProgress) {
    refreshInProgress = true;
    try {
      const renewed = await onRefreshBeforeUnauthorized();
      if (renewed) {
        unauthorizedAlreadyHandled = false;
        return;
      }
    } finally {
      refreshInProgress = false;
    }
  }

  unauthorizedAlreadyHandled = true;
  onUnauthorized();
}

function updateRetryHeaders(config: RetriableAxiosConfig) {
  const headers = config.headers as Record<string, string | undefined>;
  const sessionToken = localStorage.getItem(AUTH_KEYS.SESSION_TOKEN) || "";
  const authorization = localStorage.getItem(AUTH_KEYS.AUTHORIZATION) || "";
  const authorizationRA = localStorage.getItem(AUTH_KEYS.AUTHORIZATION_RA) || "";
  const authorizationForce = localStorage.getItem(AUTH_KEYS.AUTHORIZATION_FORCE) || "";
  const inForce = localStorage.getItem(AUTH_KEYS.IN_FORCE) === "true";

  headers["X-Session-Token"] = sessionToken;

  if ("AuthorizationRA" in headers) {
    headers.AuthorizationRA = authorizationRA;
  }

  if ("Authorization" in headers) {
    headers.Authorization = inForce
      ? authorizationForce || authorizationRA || authorization
      : authorization || authorizationRA;
  }
}

async function tryRefreshBeforeReject(): Promise<boolean> {
  if (!onRefreshBeforeUnauthorized || refreshInProgress) return false;

  refreshInProgress = true;
  try {
    const renewed = await onRefreshBeforeUnauthorized();
    if (renewed) {
      unauthorizedAlreadyHandled = false;
      return true;
    }
    return false;
  } finally {
    refreshInProgress = false;
  }
}

/**
 * Interceptor para rotas autenticadas: HTTP 401 tenta refresh (Force) antes de encerrar sessão.
 * Use `notifyOn401: false` no cliente usado só para login (`/login-tech`).
 */
export function attachUnauthorizedResponseInterceptor(
  api: AxiosInstance,
  options?: { notifyOn401?: boolean }
) {
  const notifyOn401 = options?.notifyOn401 !== false;

  api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      if (error.response?.status === 401) {
        const config = error.config as RetriableAxiosConfig | undefined;

        if (notifyOn401 && config && !config._sessionRetry) {
          const renewed = await tryRefreshBeforeReject();
          if (renewed) {
            config._sessionRetry = true;
            updateRetryHeaders(config);
            return api.request(config);
          }
        }

        if (notifyOn401 && isSessionJwtUnauthorized(error)) {
          void notifySessionUnauthorized();
        }
        return typeof window === "undefined"
          ? Promise.reject(new AuthTokenError())
          : Promise.reject(error);
      }
      return Promise.reject(error);
    }
  );
}
